import { cn } from '@/lib/utils';
import { recoverStaleSurveySession } from '@chiroup/components/body/recoverStaleSurveySession';
import { Button } from '@chiroup/components/Button';
import Combobox from '@chiroup/components/form/Combobox';
import { Input } from '@chiroup/components/form/Input';
import { InputMasked } from '@chiroup/components/form/InputMasked';
import { Phone } from '@chiroup/components/form/Phone';
import { Select } from '@chiroup/components/form/Select';
import { TextingConsentCheckboxes } from '@chiroup/components/form/TextingConsentCheckboxes';
import { Toggle } from '@chiroup/components/form/Toggle';
import { ZipCodeInput } from '@chiroup/components/form/ZipCodeInput';
import { LegalGuardianFields } from '@chiroup/components/LegalGuardianFields';
import {
  GLOBAL_COUNTRY_OPTIONS,
  GLOBAL_MORE_LANGUAGE_OPTIONS,
  GLOBAL_PREFERS_OPTIONS,
  GLOBAL_PREFIX_OPTIONS,
  GLOBAL_PRONOUN_OPTIONS,
  GLOBAL_PRONOUN_SPANSIH_OPTIONS,
  GLOBAL_RACE_OPTIONS,
  GLOBAL_RACE_SPANISH_OPTIONS,
  GLOBAL_SEX_AND_GENDER_OPTIONS,
  GLOBAL_SEX_AND_GENDER_SPANISH_OPTIONS,
  GLOBAL_SUFFIX_OPTIONS,
} from '@chiroup/core/constants/globals';
import { CANADIAN_PROVINCES, STATES } from '@chiroup/core/constants/UsStatesAndTerritories';
import { ConsentMethod } from '@chiroup/core/enums/ConsentMethod.enum';
import { Languages } from '@chiroup/core/enums/Languages.enum';
import { SmsConsentStatus } from '@chiroup/core/enums/SmsConsentStatus.enum';
import { getAgeInYears, isRealDate } from '@chiroup/core/functions/ageInYears';
import { Patient } from '@chiroup/core/types/Patient.type';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { useForm } from '@chiroup/hooks/useForm';
import { EmailValidator } from '@chiroup/hooks/validation/emailValidator';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { reportRequestSubmitError } from '../../services/reportError';
import requestService from '../../services/request.service';
import HealthcareProviderSelect from './HealthcareProviderSelect';

// Ported from healthcom-app's `BasicInformation.tsx`. Behavior, fields,
// validation, and copy are intentionally identical; the only changes
// are:
//   - props replace the legacy `MeContext` (primaryColor, clinicName)
//   - `step.complete` / `nextStep` / `complete` callback flow is
//     adapted to the schedule app's `alreadyComplete` + `onComplete`
//     contract used by `RequestRunner`.
//
// Keep this in sync with the legacy component until the legacy app
// is decommissioned — the patient-facing form is identical across
// both surfaces.

const buildValidation = (spanish: boolean) => ({
  fname: {
    required: {
      message: spanish ? 'El primer nombre legal es obligatorio.' : 'Legal first name is required.',
    },
  },
  lname: {
    required: {
      message: spanish ? 'El apellido legal es obligatorio.' : 'Legal last name is required.',
    },
  },
  email: { pattern: EmailValidator },
  address1: {
    required: {
      message: spanish ? 'La dirección es obligatoria.' : 'Address is required.',
    },
  },
  city: {
    required: { message: spanish ? 'La ciudad es obligatoria.' : 'City is required.' },
  },
  state: {
    required: {
      message: spanish ? 'El estado/provincia es obligatorio.' : 'State/Province is required.',
    },
  },
  zip: {
    function: {
      value: (value: Partial<Patient>) => {
        const { zip, country } = value;

        if (!zip) {
          return country === 'USA'
            ? spanish
              ? 'El código postal es obligatorio.'
              : 'Zip code is required.'
            : spanish
              ? 'El código postal es obligatorio.'
              : 'Postal code is required.';
        }

        if (country === 'USA' && !!zip) {
          if (zip.length !== 5 && zip.length !== 10) {
            return spanish
              ? 'El código postal debe tener 5 o 9 dígitos.'
              : 'Zip code must be 5 or 9 digits.';
          }
        }

        return false;
      },
    },
  },
  dateOfBirth: {
    function: {
      value: (values: Partial<Patient>) => {
        const { dateOfBirth } = values;

        if (dateOfBirth && !Date.parse(dateOfBirth)) {
          return spanish
            ? 'Ingrese la fecha de nacimiento en el formato DD/MM/AAAA'
            : 'Please enter date of birth in the format DD/MM/YYYY';
        }

        if (dateOfBirth?.length === 10 && Date.parse(dateOfBirth) > Date.now()) {
          return spanish
            ? 'Ingrese una fecha de nacimiento válida'
            : 'Please enter a valid date of birth';
        }

        return false;
      },
    },
  },
  socialSecurityNumber: {
    function: {
      value: (values: Partial<Patient>) => {
        const { socialSecurityNumber } = values;

        if (socialSecurityNumber && socialSecurityNumber.length !== 11) {
          return spanish
            ? 'El número de Seguro Social debe tener 9 dígitos.'
            : 'Social Security Number must be 9 digits.';
        }

        return false;
      },
    },
  },
  gender: {
    required: { message: spanish ? 'El sexo es obligatorio.' : 'Gender is required.' },
  },
  emergencyContactName: {
    required: {
      message: spanish
        ? 'El nombre del contacto de emergencia es obligatorio.'
        : 'Emergency Contact Name is required.',
    },
  },
  emergencyContactPhone: {
    required: {
      message: spanish
        ? 'El número de teléfono del contacto de emergencia es obligatorio.'
        : 'Emergency Contact Phone Number is required.',
    },
  },
  previouslyTreated: {
    required: {
      message: spanish
        ? 'Responder la pregunta sobre atención quiropráctica previa es obligatorio.'
        : 'Answering the question about previous chiropractic care is required.',
    },
  },
  phone: {
    function: {
      value: (value: Partial<Patient>) => {
        const { phone } = value;

        if (phone?.[1] === '1' && phone?.split(' ')?.[0]?.length === 6 && phone?.length < 13) {
          return spanish
            ? 'El número de teléfono debe tener 7 dígitos.'
            : 'Phone number must be 7 digits.';
        } else if (phone?.[1] === '1' && phone?.length < 13) {
          return spanish
            ? 'El número de teléfono debe tener 10 dígitos.'
            : 'Phone number must be 10 digits.';
        }

        return false;
      },
    },
  },
  legalGuardianName: {
    function: {
      value: (value: Partial<Patient>) => {
        if (value.guardian && !value.legalGuardianName) {
          return spanish
            ? 'El nombre del tutor legal es obligatorio.'
            : 'Legal guardian name is required.';
        }

        return false;
      },
    },
  },
  legalGuardianRelationship: {
    function: {
      value: (value: Partial<Patient>) => {
        if (value.guardian && !value.legalGuardianRelationship) {
          return spanish
            ? 'La relación del tutor legal es obligatoria.'
            : 'Legal guardian relationship is required.';
        }

        return false;
      },
    },
  },
});

type Props = {
  requestId: string;
  validationCode: string | number;
  language: 'en' | 'es';
  primaryColor: string;
  prMap?: { [key: string]: string };
  alreadyComplete?: boolean;
  kiosk?: boolean;
  // Context propagated from RequestRunner — needed for the texting
  // consent block and for age-based / suppress-based field rules. We
  // default missing values rather than crashing so this component can
  // be used from any caller that omits them.
  clinicId?: number;
  clinicName?: string;
  age?: number;
  smsConsent?: string | null;
  smsConsentMarketing?: string | null;
  step?: RequestStep;
  onComplete: () => void;
};

type Status = 'ready' | 'submitting' | 'submitted' | 'error';

const StepBasicInformation: React.FC<Props> = ({
  requestId,
  validationCode,
  language,
  primaryColor,
  prMap,
  alreadyComplete,
  kiosk,
  clinicId = -1,
  clinicName,
  age,
  smsConsent,
  smsConsentMarketing,
  step,
  onComplete,
}) => {
  const spanish = language === 'es';
  const [status, setStatus] = useState<Status>(alreadyComplete ? 'submitted' : 'ready');

  const { value, registerSubmit, errors, onChange, isSubmitting, patchValue } = useForm<Patient>(
    {
      language: spanish ? Languages.es : Languages.en,
      country: 'USA',
      ...(smsConsent && { smsConsent: smsConsent as SmsConsentStatus }),
      ...(smsConsentMarketing && {
        smsConsentMarketing: smsConsentMarketing as SmsConsentStatus,
      }),
    },
    buildValidation(spanish)
  );

  const onSubmit = async (val: Partial<Patient>) => {
    const provider = val.primaryProvider as any;

    if (provider?.$add) {
      val.primaryProviderText = provider?.$add;
      val.primaryProvider = undefined;
    }

    try {
      await requestService.submitInformation(requestId, val, validationCode, prMap, kiosk);
    } catch (err) {
      if (recoverStaleSurveySession({ err, kiosk, language })) {
        throw err;
      }

      reportRequestSubmitError(err, { step: 'basicInformation' });
      setStatus('error');
      throw err;
    }

    return val;
  };

  const onSuccess = async () => {
    setStatus('submitted');
  };

  useEffect(() => {
    if (alreadyComplete) setStatus('submitted');
  }, [alreadyComplete]);

  const fieldsetClassName = [
    'border-2',
    'border-gray-300',
    'rounded-md',
    'pt-2',
    'pb-4',
    'px-4',
    'mb-4',
    'space-y-1',
    'sm:space-y-0',
    'sm:grid',
    'sm:grid-cols-12',
    'sm:gap-4',
  ].join(' ');
  const legendClassName = ['text-base', 'font-medium', 'text-gray-400', 'px-2'].join(' ');

  const handleAgeChange = (e: string) => {
    patchValue({ dateOfBirth: e });

    if (isRealDate(e || '')) {
      const computedAge = getAgeInYears(e || '');

      if (computedAge < 18) patchValue({ guardian: true });
    }
  };

  const ageInYears = useMemo(() => {
    if (!value.dateOfBirth) return null;

    return getAgeInYears(value.dateOfBirth);
  }, [value.dateOfBirth]);

  const initialConsent = useRef({
    transactional: value.smsConsent === SmsConsentStatus.OPTED_IN,
    marketing: value.smsConsentMarketing === SmsConsentStatus.OPTED_IN,
    captured: false,
  });

  if (!initialConsent.current.captured) {
    initialConsent.current = {
      transactional: value.smsConsent === SmsConsentStatus.OPTED_IN,
      marketing: value.smsConsentMarketing === SmsConsentStatus.OPTED_IN,
      captured: true,
    };
  }

  if (status === 'submitted') {
    return (
      <div
        className="rounded-3xl overflow-hidden p-8 text-center flex flex-col items-center gap-3 dark:ring-1 dark:ring-white/10"
        style={{ backgroundColor: `${primaryColor}12` }}
      >
        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {spanish ? 'Información guardada' : 'Information saved'}
        </p>
        <Button text={spanish ? 'Continuar' : 'Continue'} onClick={onComplete} className="mt-2" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center px-4 sm:min-h-screen mt-8 sm:mt-0">
      <div className="max-w-4xl py-8">
        <h2 className="text-4xl tracking-tight leading-8 font-extrabold text-gray-900 sm:text-5xl sm:leading-none md:text-6xl">
          {spanish && step?.esTitle
            ? step.esTitle
            : step?.title || (spanish ? 'Sobre usted' : 'About you')}
        </h2>
        <h3 className="text-xl tracking-tight leading-6 font-extrabold text-gray-900 sm:text-2xl sm:leading-none md:text-4xl mt-2">
          <span style={{ color: primaryColor }}>
            {spanish && step?.esDescription ? step.esDescription : step?.description}
          </span>
        </h3>
        {spanish ? (
          <p className="mt-3 max-w-md text-lg text-gray-500 sm:text-xl md:mt-5 md:max-w-3xl">
            Nuestra oficina necesita una poco mas de información acerca de usted. Por favor responda
            las siguientes preguntas lo mejor que pueda.
          </p>
        ) : (
          <p className="mt-3 max-w-md text-lg text-gray-500 sm:text-xl md:mt-5 md:max-w-3xl">
            Our office needs a little more information about you. Please complete the following
            questions to the best of your ability.
          </p>
        )}

        <fieldset className={fieldsetClassName + ' mt-8'}>
          <legend className={legendClassName}>{spanish ? 'Nombre' : 'Name'}</legend>
          <Select
            name="prefix"
            className="col-span-2"
            label={spanish ? `Prefijo*` : `Prefix`}
            value={value.prefix}
            onChange={onChange('prefix')}
            errors={errors.fieldErrors?.prefix}
            options={GLOBAL_PREFIX_OPTIONS}
          />
          <Input
            name="fname"
            className="sm:col-span-3"
            label={spanish ? `Primer nombre legal*` : `Legal First Name*`}
            value={value.fname}
            onChange={onChange('fname')}
            errors={errors.fieldErrors?.fname}
          />
          <Input
            name="mname"
            className="sm:col-span-2"
            label={spanish ? 'Segundo nombre' : 'Middle Name'}
            value={value.mname}
            onChange={onChange('mname')}
            errors={errors.fieldErrors?.mname}
          />
          <Input
            name="lname"
            className="sm:col-span-3"
            label={spanish ? 'Apellido legal*' : 'Legal Last Name*'}
            value={value.lname}
            onChange={onChange('lname')}
            errors={errors.fieldErrors?.lname}
          />
          <Select
            name="suffix"
            className="col-span-2"
            label={spanish ? 'Sufijo' : 'Suffix'}
            value={value.suffix}
            onChange={onChange('suffix')}
            errors={errors.fieldErrors?.suffix}
            options={GLOBAL_SUFFIX_OPTIONS}
          />
          <Input
            name="pname"
            className="sm:col-span-6"
            label={spanish ? 'Nombre' : 'Preferred name'}
            value={value.pname}
            onChange={onChange('pname')}
            errors={errors.fieldErrors?.pname}
          />
          {!step?.suppress?.includes('gender') && (
            <>
              <Combobox
                name="pronouns"
                className={cn(value.pronouns === 'Other' ? 'col-span-3' : 'col-span-6')}
                label={spanish ? 'Pronombres' : 'Pronouns'}
                value={value.pronouns}
                onChange={val => onChange('pronouns')(val)}
                errors={errors.fieldErrors?.pronouns}
                options={spanish ? GLOBAL_PRONOUN_SPANSIH_OPTIONS : GLOBAL_PRONOUN_OPTIONS}
                limit={1}
                tooltip={value?.pronouns === 'Other' ? value?.otherPronouns : ''}
              />
              <Input
                name="otherPronouns"
                className={[
                  'sm:col-span-3',
                  ['Other'].includes(value.pronouns || '') ? 'block' : 'hidden',
                ].join(' ')}
                label={spanish ? 'Otro' : 'Other Pronouns'}
                value={value.otherPronouns}
                onChange={onChange('otherPronouns')}
                errors={errors.fieldErrors?.otherPronouns}
              />
            </>
          )}
        </fieldset>

        <fieldset className={fieldsetClassName}>
          <legend className={legendClassName}>
            {spanish ? 'Sobre el paciente' : 'About Patient'}
          </legend>
          {!step?.suppress?.includes('dateOfBirth') && (
            <InputMasked
              className="sm:col-span-12"
              name="dateOfBirth"
              label={spanish ? 'Fecha de nacimiento' : 'Date of Birth *'}
              value={value.dateOfBirth}
              onChange={onChange('dateOfBirth')}
              onBlur={handleAgeChange}
              errors={errors.fieldErrors?.dateOfBirth}
              patternFormat="##/##/####"
              placeholder="MM/DD/YYYY"
            />
          )}
          <Select
            name="language"
            className={cn(
              step?.suppress?.includes('dateOfBirth') ? 'sm:col-span-12' : 'sm:col-span-6'
            )}
            label={spanish ? 'Lengua Materna' : 'Primary Language'}
            value={value.language}
            onChange={onChange('language')}
            errors={errors.fieldErrors?.language}
            options={GLOBAL_MORE_LANGUAGE_OPTIONS}
          />
          {!step?.suppress?.includes('socialSecurityNumber') && (
            <InputMasked
              className="sm:col-span-6"
              name="socialSecurityNumber"
              label={spanish ? 'Número de seguro social' : 'Social Security Number'}
              value={value.socialSecurityNumber}
              onChange={onChange('socialSecurityNumber')}
              errors={errors.fieldErrors?.socialSecurityNumber}
              patternFormat="###-##-####"
              placeholder="###-##-####"
            />
          )}
          <Select
            name="gender"
            className="sm:col-span-3"
            label={spanish ? 'Sexo *' : 'Sex *'}
            value={value.gender}
            onChange={onChange('gender')}
            errors={errors.fieldErrors?.gender}
            options={
              spanish ? GLOBAL_SEX_AND_GENDER_SPANISH_OPTIONS : GLOBAL_SEX_AND_GENDER_OPTIONS
            }
          />
          {!step?.suppress?.includes('gender') && (
            <Input
              name="genderIdentity"
              className="sm:col-span-3"
              label={spanish ? 'Género' : 'Gender Identity'}
              value={value.genderIdentity}
              onChange={onChange('genderIdentity')}
              errors={errors?.fieldErrors?.genderIdentity}
            />
          )}
          <Select
            name="raceAndEthnicity"
            className="col-span-6"
            label={spanish ? 'Raza y etnia' : 'Race and Ethnicity'}
            value={value.raceAndEthnicity}
            onChange={onChange('raceAndEthnicity')}
            errors={errors?.fieldErrors?.raceAndEthnicity}
            options={spanish ? GLOBAL_RACE_SPANISH_OPTIONS : GLOBAL_RACE_OPTIONS}
          />
          <Input
            name="occupation"
            className="sm:col-span-6"
            label={spanish ? 'Ocupación' : 'Occupation'}
            value={value.occupation}
            onChange={onChange('occupation')}
            errors={errors.fieldErrors?.occupation}
          />
          <Input
            name="employer"
            className="sm:col-span-6"
            label={spanish ? 'Empleador' : 'Employer'}
            value={value.employer}
            onChange={onChange('employer')}
            errors={errors.fieldErrors?.employer}
          />
          <Input
            name="emergencyContactName"
            className="sm:col-span-6"
            label={spanish ? 'Nombre del contacto de emergencia *' : 'Emergency Contact Name *'}
            value={value.emergencyContactName}
            onChange={onChange('emergencyContactName')}
            errors={errors.fieldErrors?.emergencyContactName}
          />
          <Phone
            name="emergencyContactPhone"
            className="sm:col-span-6"
            label={spanish ? 'Número del contacto de emergencia *' : 'Emergency Contact Phone *'}
            value={value.emergencyContactPhone}
            onChange={onChange('emergencyContactPhone')}
            errors={errors.fieldErrors?.emergencyContactPhone}
          />
          <Toggle
            label="Requires Guardianship"
            onChange={val => {
              if (!val) {
                patchValue({ legalGuardianName: '', legalGuardianRelationship: '' });
              }

              patchValue({ guardian: val });
              onChange('guardian')(val);
            }}
            value={value.guardian || false}
            className="col-span-6 pt-3"
            disabled={!!(ageInYears && ageInYears < 18)}
            errors={errors?.fieldErrors?.guardian}
          />
          {value.guardian && (
            <LegalGuardianFields
              fieldErrors={errors?.fieldErrors}
              values={value}
              onChange={onChange}
              update
            />
          )}
          <HealthcareProviderSelect
            label={spanish ? 'Nombre de tu Doctor primario' : 'Select Your Primary Care Provider'}
            name="primaryProvider"
            className="sm:col-span-12"
            value={value.primaryProvider}
            onChange={onChange('primaryProvider')}
            errors={errors.fieldErrors?.primaryProvider}
            requestId={requestId}
          />
        </fieldset>

        <fieldset className={fieldsetClassName}>
          <legend className={legendClassName}>{spanish ? '' : 'Address'}</legend>
          <Select
            name="country"
            className="col-span-4"
            label={spanish ? 'País' : 'Country'}
            value={value?.country}
            onChange={onChange('country')}
            errors={errors.fieldErrors?.country}
            options={GLOBAL_COUNTRY_OPTIONS}
          />
          <Input
            name="address1"
            className="sm:col-span-4"
            label={spanish ? 'Dirección Principal' : 'Street Line #1 *'}
            value={value.address1}
            onChange={onChange('address1')}
            errors={errors.fieldErrors?.address1}
          />
          <Input
            name="address2"
            className="sm:col-span-4"
            label={spanish ? 'Información adicional (dirección)' : 'Street Line #2'}
            value={value.address2}
            onChange={onChange('address2')}
            errors={errors.fieldErrors?.address2}
          />
          <Input
            name="city"
            className="sm:col-span-4"
            label={spanish ? 'Ciudad *' : 'City *'}
            value={value.city}
            onChange={onChange('city')}
            errors={errors.fieldErrors?.city}
          />
          {['USA', 'CAN'].includes(value?.country || '') ? (
            <Combobox
              name="state"
              className="sm:col-span-4"
              label={`${value?.country === 'CAN' ? 'Province *' : 'State *'}`}
              value={value.state}
              onChange={onChange('state')}
              errors={errors.fieldErrors?.state}
              options={value?.country === 'CAN' ? CANADIAN_PROVINCES : STATES}
              limit={1}
            />
          ) : (
            <Input
              name="state"
              className="sm:col-span-4"
              label={spanish ? 'Estado/Provincia *' : 'State/Province *'}
              value={value.state}
              onChange={onChange('state')}
              errors={errors.fieldErrors?.state}
            />
          )}
          <ZipCodeInput
            name="zip"
            className="sm:col-span-4"
            label={spanish ? 'Código Postal *' : 'Zip/Postal code *'}
            value={value.zip}
            onChange={onChange('zip')}
            errors={errors.fieldErrors?.zip}
            country={value.country ?? ''}
          />
        </fieldset>

        <fieldset className={fieldsetClassName}>
          <legend className={legendClassName}>{spanish ? 'Comunicación' : 'Communication'}</legend>
          <Select
            name="prefers"
            className="sm:col-span-12"
            label={
              spanish ? 'Método de comunicación preferido' : 'Preferred method of communication'
            }
            value={value.prefers}
            onChange={onChange('prefers')}
            errors={errors.fieldErrors?.prefers}
            options={GLOBAL_PREFERS_OPTIONS}
          />
          {!step?.suppress?.includes('email') && (!age || age >= 18) && (
            <Input
              name="email"
              className="sm:col-span-6"
              label={spanish ? 'Correo electrónico' : 'Email'}
              value={value.email}
              onChange={onChange('email')}
              errors={errors.fieldErrors?.email}
            />
          )}
          {(!age || age >= 18) && (
            <>
              {!step?.suppress?.includes('phone') && (!age || age >= 18) && (
                <Phone
                  name="phone"
                  className="sm:col-span-6"
                  label={spanish ? 'Número de teléfono móvil' : 'Mobile number'}
                  value={value.phone}
                  onChange={onChange('phone')}
                  errors={errors.fieldErrors?.phone}
                />
              )}
              {(value.phone || step?.suppress?.includes('phone')) &&
                (!age || age >= 18) &&
                clinicId > 0 && (
                  <div className="sm:col-span-12 mt-4">
                    <TextingConsentCheckboxes
                      clinicId={clinicId}
                      transactionalConsent={value.smsConsent === SmsConsentStatus.OPTED_IN}
                      marketingConsent={value.smsConsentMarketing === SmsConsentStatus.OPTED_IN}
                      transactionalConsentStatus={value.smsConsent}
                      marketingConsentStatus={value.smsConsentMarketing}
                      initialTransactionalConsent={initialConsent.current.transactional}
                      initialMarketingConsent={initialConsent.current.marketing}
                      hideIfOptedIn={true}
                      hideMarketingConsent={true}
                      onTransactionalChange={consent =>
                        patchValue({
                          smsConsent: consent
                            ? SmsConsentStatus.OPTED_IN
                            : SmsConsentStatus.OPTED_OUT,
                        })
                      }
                      onMarketingChange={consent =>
                        patchValue({
                          smsConsentMarketing: consent
                            ? SmsConsentStatus.OPTED_IN
                            : SmsConsentStatus.OPTED_OUT,
                        })
                      }
                      transactionalErrors={errors.fieldErrors?.smsConsent}
                      marketingErrors={errors.fieldErrors?.smsConsentMarketing}
                      clinicName={clinicName || 'the clinic'}
                      showConsentScript={false}
                      consentMethod={ConsentMethod.DIGITAL_SIGNATURE}
                      patientView={true}
                    />
                  </div>
                )}
              <Phone
                name="workPhone"
                className="sm:col-span-6"
                label={spanish ? 'Número de teléfono de trabajo' : 'Work number'}
                value={value.workPhone}
                onChange={onChange('workPhone')}
                errors={errors.fieldErrors?.workNumber}
              />
              <Phone
                name="otherPhone"
                className="sm:col-span-6"
                label={spanish ? 'Otro número de teléfono' : 'Other number'}
                value={value.otherPhone}
                onChange={onChange('otherPhone')}
                errors={errors.fieldErrors?.otherNumber}
              />
            </>
          )}
        </fieldset>

        <fieldset className={fieldsetClassName}>
          <legend className={legendClassName}>{spanish ? 'Otro' : 'Other'}</legend>
          <Select
            name="previouslyTreated"
            className="sm:col-span-12"
            inlineSelect={true}
            inlineLabelWidth="w-3/5"
            inlineInputWidth="w-2/5"
            label={
              spanish
                ? 'Alguna vez te has tratado con un Quiropráctico?'
                : 'Have you had chiropractic treatment before? *'
            }
            value={value.previouslyTreated}
            onChange={onChange('previouslyTreated')}
            errors={errors.fieldErrors?.previouslyTreated}
            options={
              spanish
                ? [
                    { text: 'Sí', value: true },
                    { text: 'No', value: false },
                  ]
                : [
                    { text: 'Yes', value: true },
                    { text: 'No', value: false },
                  ]
            }
          />
          <Select
            name="heardAboutUs"
            className="sm:col-span-12"
            label={
              spanish ? 'Como escuchaste de nuestra clínica?' : 'How did you hear about our office?'
            }
            value={value.heardAboutUs}
            inlineSelect={true}
            inlineLabelWidth="w-3/5"
            inlineInputWidth="w-2/5"
            onChange={onChange('heardAboutUs')}
            errors={errors.fieldErrors?.heardAboutUs}
            options={
              spanish
                ? [
                    { text: 'Otro paciente o individuo', value: 'Another patient or individual' },
                    { text: 'Médico/a', value: 'Doctor' },
                    { text: 'Abogado', value: 'Attorney' },
                    {
                      text: 'Directorio de compañía de seguros',
                      value: 'Insurance company directory',
                    },
                    { text: 'Sitio web de la clínica', value: 'Practice website' },
                    { text: 'Página de redes sociales', value: 'Social media page' },
                    { text: 'Letrero/visto al pasar', value: 'Sign/drive by' },
                    { text: 'Otro', value: 'Other' },
                  ]
                : [
                    {
                      text: 'Another patient or individual',
                      value: 'Another patient or individual',
                    },
                    { text: 'Doctor', value: 'Doctor' },
                    { text: 'Attorney', value: 'Attorney' },
                    { text: 'Insurance company directory', value: 'Insurance company directory' },
                    { text: 'Practice website', value: 'Practice website' },
                    { text: 'Social media page', value: 'Social media page' },
                    { text: 'Sign/drive by', value: 'Sign/drive by' },
                    { text: 'Other', value: 'Other' },
                  ]
            }
          />
          {value.heardAboutUs === 'Other' && (
            <Input
              name="heardAboutUsDetail"
              inline={true}
              inlineLabelWidth="w-3/5"
              inlineInputWidth="w-2/5"
              className="sm:col-span-12"
              label={spanish ? '¿Cómo se enteró de nosotros?' : 'How did you hear about us?'}
              value={value.heardAboutUsDetail}
              onChange={onChange('heardAboutUsDetail')}
              errors={errors.fieldErrors?.heardAboutUsDetail}
            />
          )}
        </fieldset>

        <div className="mt-8">
          <Button
            text={spanish ? 'Ingresar' : 'Submit'}
            onClick={registerSubmit(onSubmit, { onSuccess })}
            loading={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default StepBasicInformation;
